import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";

/* Founder Portal → /admin/academy-logic
   CMS for the Stoic Logic course: lessons, phases, course meta, terms,
   Chamber. Every save validates server side, writes a revision snapshot,
   and marks the course founder_edited so reseeding never overwrites. */

const en = (v) => (v && typeof v === "object" ? v.en || "" : v || "");
const TABS = ["Lessons", "Phases", "Course", "Terms", "Chamber", "History"];

function Text({ label, value, onChange, rows = 2, testid }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.2em] uppercase text-bone/50 mb-1.5">{label}</label>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black/30 border border-white/15 text-bone text-[13px] font-reading p-3 focus:outline-none focus:border-gold/60" />
    </div>
  );
}

function SaveBar({ onSave, saving, err, ok }) {
  return (
    <div className="flex items-center gap-4 pt-2">
      <button data-testid="cms-save" onClick={onSave} disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-40">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
      </button>
      {ok && <span className="text-emerald-300 text-xs" data-testid="cms-save-ok">Saved</span>}
      {err && <span className="text-red-300 text-xs whitespace-pre-line" data-testid="cms-save-error">{err}</span>}
    </div>
  );
}

function useSaver(fn, reload) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const save = async (...args) => {
    setSaving(true); setErr(""); setOk(false);
    try {
      await fn(...args);
      setOk(true); setTimeout(() => setOk(false), 2500);
      reload && reload();
    } catch (e) {
      const d = e.detail;
      setErr(d && typeof d === "object" && d.problems ? `${d.message}:\n${d.problems.join("\n")}` : e.message || "Save failed");
    } finally { setSaving(false); }
  };
  return { save, saving, err, ok };
}

function LessonEditor({ course, reload }) {
  const [phaseSlug, setPhaseSlug] = useState(course.phases[0].slug);
  const phase = course.phases.find((p) => p.slug === phaseSlug) || course.phases[0];
  const [lessonSlug, setLessonSlug] = useState(phase.lessons[0].slug);
  const lesson = phase.lessons.find((l) => l.slug === lessonSlug) || phase.lessons[0];
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(false);
  const saver = useSaver((payload) => adminApi.putLogicLesson(phase.slug, lesson.slug, payload), reload);

  useEffect(() => {
    if (!lesson) return;
    setForm({
      title: en(lesson.title), thresholdSentence: en(lesson.thresholdSentence),
      learningObjective: en(lesson.learningObjective), openingScenario: en(lesson.openingScenario),
      scenarioPrompt: en(lesson.scenarioPrompt), coreInsight: en(lesson.coreInsight),
      mentor: lesson.mentor, mentorQuestion: en(lesson.mentorQuestion),
      practiceAssignment: en(lesson.practiceAssignment), masteryCriterion: en(lesson.masteryCriterion),
      reflectionQuestions: (lesson.reflectionQuestions || []).map(en).join("\n"),
      depthEssential: en(lesson.depthLayers?.essential), depthAcademic: en(lesson.depthLayers?.academic),
      depthSources: en(lesson.depthLayers?.sources),
      exercise: JSON.stringify(lesson.exercise, null, 2),
      publicationStatus: lesson.publicationStatus || "published",
    });
  }, [phaseSlug, lessonSlug, course]); // eslint-disable-line

  if (!form) return null;
  const f = (k) => (v) => setForm((x) => ({ ...x, [k]: v }));

  const onSave = () => {
    let exercise;
    try { exercise = JSON.parse(form.exercise); }
    catch { saver.save && (saver.err = ""); return alert("Exercise is not valid JSON."); }
    saver.save({
      title: form.title, thresholdSentence: form.thresholdSentence,
      learningObjective: form.learningObjective, openingScenario: form.openingScenario,
      scenarioPrompt: form.scenarioPrompt, coreInsight: form.coreInsight,
      mentor: form.mentor, mentorQuestion: form.mentorQuestion,
      practiceAssignment: form.practiceAssignment, masteryCriterion: form.masteryCriterion,
      reflectionQuestions: form.reflectionQuestions.split("\n").filter((q) => q.trim()),
      depthLayers: { essential: form.depthEssential, academic: form.depthAcademic, sources: form.depthSources },
      exercise, publicationStatus: form.publicationStatus,
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3">
        <select value={phaseSlug} data-testid="cms-phase-select"
          onChange={(e) => { setPhaseSlug(e.target.value); setLessonSlug(course.phases.find((p) => p.slug === e.target.value).lessons[0].slug); }}
          className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
          {course.phases.map((p) => <option key={p.slug} value={p.slug}>Phase {p.order}: {en(p.title)}</option>)}
        </select>
        <select value={lessonSlug} data-testid="cms-lesson-select" onChange={(e) => setLessonSlug(e.target.value)}
          className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
          {phase.lessons.map((l) => <option key={l.slug} value={l.slug}>Lesson {l.order}: {en(l.title)}</option>)}
        </select>
        <select value={form.publicationStatus} data-testid="cms-lesson-status" onChange={(e) => f("publicationStatus")(e.target.value)}
          className="bg-black/30 border border-gold/40 text-gold text-[13px] px-3 py-2">
          {["draft", "review", "published"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setPreview(!preview)} data-testid="cms-lesson-preview-toggle"
          className="text-[11px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 px-3 py-2 hover:border-gold/40">{preview ? "Edit" : "Preview"}</button>
        <a href={`/academy/logic/phase/${phase.slug}/lesson/${lesson.slug}`} target="_blank" rel="noreferrer"
          className="text-[11px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 px-3 py-2 hover:border-gold/40">View live</a>
      </div>

      {preview ? (
        <div className="border border-gold/30 bg-ink p-8 space-y-6" data-testid="cms-lesson-preview">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8]">Phase {phase.order} · Lesson {lesson.order}</p>
          <h2 className="font-serif text-3xl text-bone">{form.title}</h2>
          <p className="font-serif italic text-lg text-gold/90">{form.thresholdSentence}</p>
          <p className="text-sm text-fog/70">Skill in training: {form.learningObjective}</p>
          <p className="font-reading text-fog leading-[1.9]">{form.openingScenario}</p>
          <p className="text-sm text-bone/90 font-reading">{form.scenarioPrompt}</p>
          <div className="border-l-2 border-gold/50 pl-5"><p className="font-serif text-xl text-bone">{form.coreInsight}</p></div>
          <div className="border border-[#8fa3c8]/25 bg-[#1a2130] p-5"><p className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8] mb-1 capitalize">{form.mentor}</p><p className="font-serif italic text-bone/95">{form.mentorQuestion}</p></div>
          <p className="text-sm text-fog font-reading">{form.practiceAssignment}</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-4">
            <Text label="Title" value={form.title} onChange={f("title")} rows={1} testid="cms-lesson-title" />
            <Text label="Threshold sentence" value={form.thresholdSentence} onChange={f("thresholdSentence")} testid="cms-lesson-threshold" />
            <Text label="Learning objective" value={form.learningObjective} onChange={f("learningObjective")} />
            <Text label="Opening scenario" value={form.openingScenario} onChange={f("openingScenario")} rows={4} />
            <Text label="Scenario prompt" value={form.scenarioPrompt} onChange={f("scenarioPrompt")} />
            <Text label="Core insight" value={form.coreInsight} onChange={f("coreInsight")} rows={3} />
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-bone/50 mb-1.5">Mentor</label>
              <select value={form.mentor} onChange={(e) => f("mentor")(e.target.value)} data-testid="cms-lesson-mentor"
                className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
                {["marcus", "epictetus", "seneca"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <Text label="Mentor question" value={form.mentorQuestion} onChange={f("mentorQuestion")} />
          </div>
          <div className="space-y-4">
            <Text label="Practice assignment" value={form.practiceAssignment} onChange={f("practiceAssignment")} rows={3} />
            <Text label="Reflection questions (one per line)" value={form.reflectionQuestions} onChange={f("reflectionQuestions")} rows={3} />
            <Text label="Depth layer: essential" value={form.depthEssential} onChange={f("depthEssential")} rows={3} />
            <Text label="Depth layer: academic" value={form.depthAcademic} onChange={f("depthAcademic")} rows={3} />
            <Text label="Depth layer: sources" value={form.depthSources} onChange={f("depthSources")} rows={2} />
            <Text label="Mastery criterion" value={form.masteryCriterion} onChange={f("masteryCriterion")} rows={2} />
            <Text label="Exercise (JSON: type, instruction, items with text/answer)" value={form.exercise} onChange={f("exercise")} rows={10} testid="cms-lesson-exercise" />
          </div>
        </div>
      )}
      <SaveBar onSave={onSave} saving={saver.saving} err={saver.err} ok={saver.ok} />
    </div>
  );
}

function PhaseEditor({ course, reload }) {
  const [slug, setSlug] = useState(course.phases[0].slug);
  const phase = course.phases.find((p) => p.slug === slug);
  const [form, setForm] = useState(null);
  const saver = useSaver((payload) => adminApi.putLogicPhase(slug, payload), reload);
  useEffect(() => {
    setForm({ title: en(phase.title), centralQuestion: en(phase.centralQuestion), coreAbility: en(phase.coreAbility), recognition: phase.recognition || "" });
  }, [slug, course]); // eslint-disable-line
  if (!form) return null;
  const f = (k) => (v) => setForm((x) => ({ ...x, [k]: v }));
  return (
    <div className="max-w-2xl space-y-4">
      <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="cms-phases-select"
        className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
        {course.phases.map((p) => <option key={p.slug} value={p.slug}>Phase {p.order}: {en(p.title)}</option>)}
      </select>
      <Text label="Title" value={form.title} onChange={f("title")} rows={1} />
      <Text label="Central question" value={form.centralQuestion} onChange={f("centralQuestion")} />
      <Text label="Core ability" value={form.coreAbility} onChange={f("coreAbility")} />
      <Text label="Recognition (awarded on completion)" value={form.recognition} onChange={f("recognition")} rows={1} />
      <SaveBar onSave={() => saver.save(form)} saving={saver.saving} err={saver.err} ok={saver.ok} />
    </div>
  );
}

function CourseEditor({ course, reload }) {
  const FIELDS = ["title", "subtitle", "hallName", "theme", "introduction", "heroIntro", "thresholdLine"];
  const [form, setForm] = useState(null);
  const saver = useSaver((payload) => adminApi.putLogicCourse(payload), reload);
  useEffect(() => { setForm(Object.fromEntries(FIELDS.map((k) => [k, en(course[k])]))); }, [course]); // eslint-disable-line
  if (!form) return null;
  return (
    <div className="max-w-2xl space-y-4">
      {FIELDS.map((k) => (
        <Text key={k} label={k} value={form[k]} onChange={(v) => setForm((x) => ({ ...x, [k]: v }))} rows={k === "introduction" ? 3 : 2} />
      ))}
      <SaveBar onSave={() => saver.save(form)} saving={saver.saving} err={saver.err} ok={saver.ok} />
    </div>
  );
}

function TermsEditor({ terms, reload }) {
  const [id, setId] = useState(terms[0]?.id);
  const term = terms.find((t) => t.id === id) || terms[0];
  const [form, setForm] = useState(null);
  const saver = useSaver((payload) => adminApi.putLogicTerm(term.id, payload), reload);
  useEffect(() => {
    if (!term) return;
    setForm({ original: term.original, transliteration: term.transliteration,
      plain: en(term.plain), academic: en(term.academic), application: en(term.application), example: en(term.example) });
  }, [id, terms]); // eslint-disable-line
  if (!form) return null;
  const f = (k) => (v) => setForm((x) => ({ ...x, [k]: v }));
  return (
    <div className="max-w-2xl space-y-4">
      <select value={id} onChange={(e) => setId(e.target.value)} data-testid="cms-terms-select"
        className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
        {terms.map((t) => <option key={t.id} value={t.id}>{t.transliteration}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-4">
        <Text label="Original" value={form.original} onChange={f("original")} rows={1} />
        <Text label="Transliteration" value={form.transliteration} onChange={f("transliteration")} rows={1} />
      </div>
      <Text label="Plain explanation" value={form.plain} onChange={f("plain")} rows={2} />
      <Text label="Academic definition" value={form.academic} onChange={f("academic")} rows={3} />
      <Text label="Application in Stoic Logic" value={form.application} onChange={f("application")} rows={2} />
      <Text label="Concrete example" value={form.example} onChange={f("example")} rows={2} />
      <SaveBar onSave={() => saver.save(form)} saving={saver.saving} err={saver.err} ok={saver.ok} />
    </div>
  );
}

function ChamberEditor({ chamber, reload }) {
  const FIELDS = ["title", "intro", "pressure", "closing", "recognitionTitle", "recognitionText"];
  const [form, setForm] = useState(null);
  const saver = useSaver((payload) => adminApi.putLogicChamber(payload), reload);
  useEffect(() => { setForm(Object.fromEntries(FIELDS.map((k) => [k, en(chamber[k])]))); }, [chamber]); // eslint-disable-line
  if (!form) return null;
  return (
    <div className="max-w-2xl space-y-4">
      <p className="text-xs text-fog/60 font-reading">The case stages and testimonies are structural; edit their texts here, and contact development for structural changes to the stages themselves.</p>
      {FIELDS.map((k) => (
        <Text key={k} label={k} value={form[k]} onChange={(v) => setForm((x) => ({ ...x, [k]: v }))} rows={k === "intro" ? 4 : 2} />
      ))}
      <SaveBar onSave={() => saver.save(form)} saving={saver.saving} err={saver.err} ok={saver.ok} />
    </div>
  );
}

function History() {
  const [key, setKey] = useState("");
  const [revisions, setRevisions] = useState([]);
  const load = useCallback(() => { adminApi.getLogicRevisions(key).then((d) => setRevisions(d.revisions || [])); }, [key]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="max-w-2xl space-y-4">
      <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="Filter by entity key, e.g. what-appears/the-first-impression"
        data-testid="cms-history-filter"
        className="w-full bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2 focus:outline-none focus:border-gold/60" />
      <div className="space-y-2" data-testid="cms-history-list">
        {revisions.length === 0 && <p className="text-fog/50 text-sm font-reading italic">No revisions recorded yet. Every save writes one.</p>}
        {revisions.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 border border-white/10 px-4 py-2.5 text-[13px]">
            <span className="text-bone/80">{r.entityType} · <span className="font-mono text-gold/80">{r.entityKey}</span></span>
            <span className="text-fog/50">{String(r.savedAt).slice(0, 19).replace("T", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminLogicCMS() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Lessons");
  const load = useCallback(() => { adminApi.getLogicFull().then(setData).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const body = useMemo(() => {
    if (!data?.course) return null;
    if (tab === "Lessons") return <LessonEditor course={data.course} reload={load} />;
    if (tab === "Phases") return <PhaseEditor course={data.course} reload={load} />;
    if (tab === "Course") return <CourseEditor course={data.course} reload={load} />;
    if (tab === "Terms") return <TermsEditor terms={data.terms || []} reload={load} />;
    if (tab === "Chamber") return <ChamberEditor chamber={data.chamber} reload={load} />;
    return <History />;
  }, [data, tab, load]);

  return (
    <AdminShell title="Stoic Logic · Course CMS">
      <div data-testid="admin-logic-cms-page">
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t} data-testid={`cms-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
              className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border ${tab === t ? "border-gold/60 text-gold bg-gold/5" : "border-white/10 text-bone/50 hover:border-gold/30"}`}>{t}</button>
          ))}
          <Link to="/admin/academy-logic-media" className="ml-auto text-[11px] tracking-[0.2em] uppercase text-bone/50 border border-white/10 px-4 py-2 hover:border-gold/30">Media slots</Link>
        </div>
        {body || <p className="text-fog/50 text-sm">Loading course…</p>}
      </div>
    </AdminShell>
  );
}
